(function () {
  'use strict';

  var SHIPPING_TAG = 'shipping';
  var FUNDRAISER_TAG = 'fundraiser';
  var QUOTE_MAX_QTY = 7;
  var SHIPPING_DATE_LABEL = 'Requested Ship Date';
  var productTagCache = {};

  var FUNDRAISER_ATTR_KEYS = [
    'Fundraiser Name',
    'Fundraiser Collection URL',
    'Student Name',
    'Fundraiser Pickup Date Display',
    'Fundraiser Pickup Location',
    'Order Due Date',
    'Order Due Time',
    'Order Fulfillment Type',
    'Order Location'
  ];

  var pickeasySelectors = [
    'pickeasy-app-widget',
    'pickeasy-store-pickup',
    'pickeasy-local-delivery',
    'pickeasy-slot-picker',
    'pickeasy-pickup-popup',
    '[class*="pickeasy"]',
    '[id*="pickeasy"]',
    '[class*="pc-widget"]',
    '[id*="pc-widget"]',
    '[data-pc-widget]',
    '[data-pickeasy]',
    '[class*="order-delivery-date"]',
    '[id*="order-delivery-date"]'
  ];

  var pickupLocalSelectors = [
    'pickeasy-store-pickup',
    'pickeasy-local-delivery',
    'pickeasy-pickup-popup'
  ];

  var state = {
    fundraiserOnly: false,
    shippingOnly: false,
    blockReason: null,
    pickeasyObserver: null,
    hideRetryTimer: null,
    lastCart: null,
    lastClassification: null
  };

  var cartFetchPromise = null;
  var cartFetchCache = null;
  var syncDebounceTimer = null;
  var fundraiserConfigCache = {};

  function tagMap() {
    return (window.ssbCartFulfillment && window.ssbCartFulfillment.tagMap) || {};
  }

  function variantTagEntry(variantId) {
    var map = tagMap();
    var entry = map[String(variantId)];

    if (entry && typeof entry === 'object') {
      return {
        shipping: !!entry.shipping,
        fundraiser: !!entry.fundraiser
      };
    }

    if (typeof entry === 'boolean') {
      return { shipping: entry, fundraiser: false };
    }

    return null;
  }

  function itemTitleLooksFundraiser(item) {
    return item.title.toLowerCase().indexOf('(fundraiser)') !== -1;
  }

  function itemIsFundraiserSync(item) {
    var entry = variantTagEntry(item.variant_id);
    if (entry) return entry.fundraiser;
    return itemTitleLooksFundraiser(item);
  }

  function isFundraiserOnlyCart(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return false;
    return cart.items.every(itemIsFundraiserSync);
  }

  function hasNonFundraiserItemSync(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return false;
    return cart.items.some(function (item) {
      return !itemIsFundraiserSync(item);
    });
  }

  function productHasTag(tags, tagName) {
    if (!tags || !tags.length) return false;
    return tags.some(function (tag) {
      return String(tag).toLowerCase() === tagName;
    });
  }

  function productHasShippingTag(tags) {
    return productHasTag(tags, SHIPPING_TAG);
  }

  function productHasFundraiserTag(tags) {
    return productHasTag(tags, FUNDRAISER_TAG);
  }

  function fetchProductTags(handle) {
    if (!handle) return Promise.resolve([]);
    if (productTagCache[handle]) return Promise.resolve(productTagCache[handle]);

    return fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (response) {
        if (!response.ok) throw new Error('product fetch failed');
        return response.json();
      })
      .then(function (product) {
        productTagCache[handle] = product.tags || [];
        return productTagCache[handle];
      })
      .catch(function () {
        productTagCache[handle] = [];
        return [];
      });
  }

  function classifyItemTags(item) {
    var map = tagMap();
    var variantId = String(item.variant_id);
    var known = variantTagEntry(item.variant_id);

    if (known) {
      return Promise.resolve({
        isShipping: known.shipping,
        isFundraiser: known.fundraiser
      });
    }

    return fetchProductTags(item.handle).then(function (tags) {
      var isShipping = productHasShippingTag(tags);
      var isFundraiser = productHasFundraiserTag(tags);
      map[variantId] = { shipping: isShipping, fundraiser: isFundraiser };
      return { isShipping: isShipping, isFundraiser: isFundraiser };
    });
  }

  function isVariantShipping(item) {
    return classifyItemTags(item).then(function (entry) {
      return entry.isShipping;
    });
  }

  function classifyCart(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return Promise.resolve({
        fundraiserOnly: false,
        hasFundraiserItems: false,
        hasShippingItems: false,
        hasNonShippingItems: false,
        shippingQty: 0,
        blockReason: null
      });
    }

    return Promise.all(
      cart.items.map(function (item) {
        return classifyItemTags(item).then(function (entry) {
          return { item: item, isShipping: entry.isShipping, isFundraiser: entry.isFundraiser };
        });
      })
    ).then(function (entries) {
      var hasFundraiserItems = false;
      var hasNonFundraiserItems = false;
      var hasShippingItems = false;
      var hasNonShippingItems = false;
      var shippingQty = 0;

      entries.forEach(function (entry) {
        if (entry.isFundraiser) hasFundraiserItems = true;
        else hasNonFundraiserItems = true;

        if (entry.isShipping) {
          hasShippingItems = true;
          shippingQty += entry.item.quantity;
        } else {
          hasNonShippingItems = true;
        }
      });

      if (hasFundraiserItems && hasNonFundraiserItems) {
        return {
          fundraiserOnly: false,
          hasFundraiserItems: true,
          hasShippingItems: hasShippingItems,
          hasNonShippingItems: hasNonShippingItems,
          shippingQty: shippingQty,
          blockReason: 'fundraiser-mixed'
        };
      }

      if (hasFundraiserItems && !hasNonFundraiserItems) {
        return {
          fundraiserOnly: true,
          hasFundraiserItems: true,
          hasShippingItems: false,
          hasNonShippingItems: false,
          shippingQty: 0,
          blockReason: null
        };
      }

      var blockReason = null;
      if (hasShippingItems && hasNonShippingItems) {
        blockReason = 'mixed';
      } else if (hasShippingItems && shippingQty >= QUOTE_MAX_QTY) {
        blockReason = 'quote';
      }

      return {
        fundraiserOnly: false,
        hasFundraiserItems: false,
        hasShippingItems: hasShippingItems,
        hasNonShippingItems: hasNonShippingItems,
        shippingQty: shippingQty,
        blockReason: blockReason
      };
    });
  }

  function hidePickeasy(scope) {
    var root = scope || document;
    pickeasySelectors.forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (el) {
        el.style.setProperty('display', 'none', 'important');
      });
    });

    root.querySelectorAll('cart-drawer .drawer__footer > *').forEach(function (el) {
      if (
        el.matches(
          '.cart-drawer__footer, .ssb-drawer-back, .cart__ctas, details, cart-note, .ssb-cart-fulfillment-alerts, [data-ssb-cart-alerts], .ssb-fundraiser-pickup-panel, [data-ssb-fundraiser-pickup-panel]'
        )
      ) {
        return;
      }
      el.style.setProperty('display', 'none', 'important');
    });
  }

  function showPickeasy(scope) {
    var root = scope || document;
    pickeasySelectors.forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (el) {
        el.style.removeProperty('display');
      });
    });

    root.querySelectorAll('cart-drawer .drawer__footer > *').forEach(function (el) {
      if (
        el.matches(
          '.cart-drawer__footer, .ssb-drawer-back, .cart__ctas, details, cart-note, .ssb-cart-fulfillment-alerts, [data-ssb-cart-alerts], .ssb-fundraiser-pickup-panel, [data-ssb-fundraiser-pickup-panel]'
        )
      ) {
        return;
      }
      el.style.removeProperty('display');
    });
  }

  function clearPickupLocalHide(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-ssb-pickup-suppressed]').forEach(function (el) {
      el.style.removeProperty('display');
      delete el.dataset.ssbPickupSuppressed;
    });
  }

  function hidePickupLocationSections(root) {
    var containers = root.querySelectorAll(
      'cart-drawer .drawer__footer, .cart__blocks, .main-cart-footer, #main-cart-footer'
    );

    containers.forEach(function (container) {
      container.querySelectorAll('*').forEach(function (el) {
        if (el.dataset.ssbPickupSuppressed) return;
        if (el.children.length > 0) return;

        var label = el.textContent.trim();
        if (!/^pickup locations?$/i.test(label)) return;

        var target = el.parentElement;
        if (!target || target === container) return;

        target.style.setProperty('display', 'none', 'important');
        target.dataset.ssbPickupSuppressed = '1';
      });
    });
  }

  function hidePickupAndLocal(scope) {
    var root = scope || document;

    pickupLocalSelectors.forEach(function (sel) {
      root.querySelectorAll(sel).forEach(function (el) {
        el.style.setProperty('display', 'none', 'important');
        el.dataset.ssbPickupSuppressed = '1';
      });
    });

    hidePickupLocationSections(root);
  }

  function updateShippingDateLabel(scope) {
    if (!state.shippingOnly) return;

    var root = scope || document;
    var containers = root.querySelectorAll(
      'cart-drawer .drawer__footer, .cart__blocks, .main-cart-footer, #main-cart-footer, pickeasy-app-widget, pickeasy-slot-picker'
    );

    containers.forEach(function (container) {
      container.querySelectorAll('*').forEach(function (el) {
        if (el.children.length > 0) return;

        var text = el.textContent.trim();
        if (!/^when would you like to get this order\?$/i.test(text)) return;

        el.textContent = SHIPPING_DATE_LABEL;
        el.dataset.ssbShippingDateLabel = '1';
      });
    });
  }

  function applyShippingOnlyPickeasyUi(scope) {
    hidePickupAndLocal(scope);
    updateShippingDateLabel(scope);
  }

  function setShippingOnlyMode(enabled) {
    var drawer = document.querySelector('cart-drawer');
    var cartItems = document.querySelector('cart-items');

    if (drawer) drawer.classList.toggle('ssb-shipping-only', enabled);
    if (cartItems) cartItems.classList.toggle('ssb-shipping-only', enabled);
    document.body.classList.toggle('ssb-shipping-only-cart', enabled);
  }

  function setFundraiserPickupMode(enabled) {
    var drawer = document.querySelector('cart-drawer');
    var cartItems = document.querySelector('cart-items');

    if (drawer) drawer.classList.toggle('ssb-fundraiser-pickup', enabled);
    if (cartItems) cartItems.classList.toggle('ssb-fundraiser-pickup', enabled);
    document.body.classList.toggle('ssb-fundraiser-pickup-cart', enabled);
  }

  function isShippingOnlyCart(classification) {
    return (
      !classification.fundraiserOnly &&
      !classification.blockReason &&
      classification.hasShippingItems &&
      !classification.hasNonShippingItems
    );
  }

  function clearStaleFundraiserAttributes(cart) {
    if (!cart || !cart.attributes) return Promise.resolve(cart);

    var attrs = cart.attributes;
    var hasFundraiserAttrs = FUNDRAISER_ATTR_KEYS.some(function (key) {
      return !!attrs[key];
    });

    if (!hasFundraiserAttrs) return Promise.resolve(cart);

    var cleared = {};
    FUNDRAISER_ATTR_KEYS.forEach(function (key) {
      cleared[key] = '';
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: cleared })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (updatedCart) {
        setCartCache(updatedCart);
        syncFundraiserCheckoutFormFields(updatedCart.attributes || cleared);
        return updatedCart;
      });
  }

  function shouldClearFundraiserAttributes(cart, classification) {
    if (!cart || !cart.attributes) return false;

    var hasFundraiserAttrs = FUNDRAISER_ATTR_KEYS.some(function (key) {
      return !!cart.attributes[key];
    });

    if (!hasFundraiserAttrs) return false;
    return !classification.hasFundraiserItems;
  }

  function notifyCartContentsRefresh(cart) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'ssb-cart-fulfillment', cartData: cart });
    }
  }

  function isCartEmpty() {
    return !!(
      document.querySelector('cart-drawer.is-empty') ||
      document.querySelector('cart-items.is-empty')
    );
  }

  function setCheckoutDisabled(disabled, reason) {
    ['checkout', 'CartDrawer-Checkout'].forEach(function (id) {
      var btn = document.getElementById(id);
      if (!btn) return;

      if (disabled) {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
        btn.dataset.ssbShippingBlocked = reason || '1';
        return;
      }

      if (!btn.dataset.ssbShippingBlocked) return;

      delete btn.dataset.ssbShippingBlocked;

      if (isCartEmpty()) {
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('aria-disabled', 'true');
        return;
      }

      btn.removeAttribute('disabled');
      btn.removeAttribute('aria-disabled');
    });
  }

  function updateAlerts(blockReason) {
    document.querySelectorAll('[data-ssb-cart-alerts]').forEach(function (container) {
      var mixed = container.querySelector('.ssb-cart-alert-mixed');
      var quote = container.querySelector('.ssb-cart-alert-quote');
      var fundraiserMixed = container.querySelector('.ssb-cart-alert-fundraiser-mixed');
      if (mixed) mixed.hidden = blockReason !== 'mixed';
      if (quote) quote.hidden = blockReason !== 'quote';
      if (fundraiserMixed) fundraiserMixed.hidden = blockReason !== 'fundraiser-mixed';
    });
  }

  function bindFundraiserCheckoutOverride() {
    bindFundraiserCartFormCheckout();

    [
      { id: 'checkout', active: function () { return shouldUseFundraiserCheckout(); } },
      { id: 'CartDrawer-Checkout', active: function () { return shouldUseFundraiserCheckout(); } }
    ].forEach(function (cfg) {
      var btn = document.getElementById(cfg.id);
      if (!btn || btn.dataset.ssbFundraiserBound) return;
      btn.dataset.ssbFundraiserBound = '1';
      btn.addEventListener(
        'click',
        function (e) {
          if (btn.dataset.ssbNativeCheckout === '1') return;
          if (!cfg.active()) return;
          e.stopImmediatePropagation();
          e.preventDefault();
          redirectFundraiserCheckout(btn);
        },
        true
      );
    });
  }

  function pageLooksFundraiserCheckout() {
    if (state.fundraiserOnly) return true;
    if (document.body.classList.contains('ssb-fundraiser-only-cart')) return true;
    if (document.querySelector('[data-ssb-fundraiser-pickup-panel], .ssb-fundraiser-pickup-panel')) {
      return true;
    }
    return cartIsFundraiserCheckout(state.lastCart || cartFetchCache);
  }

  function shouldUseFundraiserCheckout() {
    return pageLooksFundraiserCheckout();
  }

  function redirectFundraiserCheckout(btn) {
    if (btn) {
      btn.setAttribute('disabled', 'disabled');
      btn.setAttribute('aria-disabled', 'true');
    }

    invalidateCartCache();

    prepareFundraiserCheckout()
      .then(function (cart) {
        submitFundraiserCheckout(cart);
      })
      .catch(function () {
        if (btn) {
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
        }
        alert(
          'We could not prepare your fundraiser order for checkout. Please return to the cart and try again, or contact the bakery for help.'
        );
      });
  }

  function submitFundraiserCheckout(cart) {
    var attrs = buildFullFundraiserAttributesFromCart(cart || cartFetchCache || {}, null);
    syncFundraiserCheckoutFormFields(attrs);

    var cartForm = document.getElementById('cart');
    var checkoutBtn = document.getElementById('checkout');

    if (!cartForm || !checkoutBtn) {
      window.location.href = '/checkout';
      return;
    }

    cartForm.dataset.ssbAllowNativeCheckout = '1';
    checkoutBtn.disabled = false;
    checkoutBtn.removeAttribute('disabled');
    checkoutBtn.removeAttribute('aria-disabled');
    cartForm.requestSubmit(checkoutBtn);
  }

  function bindFundraiserCartFormCheckout() {
    var cartForm = document.getElementById('cart');
    if (!cartForm || cartForm.dataset.ssbFundraiserCheckoutBound) return;
    cartForm.dataset.ssbFundraiserCheckoutBound = '1';

    cartForm.addEventListener(
      'submit',
      function (e) {
        if (cartForm.dataset.ssbAllowNativeCheckout === '1') {
          delete cartForm.dataset.ssbAllowNativeCheckout;
          return;
        }

        var submitter = e.submitter;
        if (!submitter || submitter.name !== 'checkout') return;

        e.preventDefault();
        e.stopImmediatePropagation();

        fetchCart().then(function (cart) {
          setCartCache(cart);
          if (!cartIsFundraiserCheckout(cart) && !isFundraiserOnlyCart(cart)) {
            cartForm.dataset.ssbAllowNativeCheckout = '1';
            cartForm.requestSubmit(submitter);
            return;
          }
          redirectFundraiserCheckout(submitter);
        });
      },
      true
    );
  }

  function parseFundraiserConfigFromDocument(doc) {
    var el = doc.getElementById('ssb-fundraiser-page-config');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  }

  function fetchFundraiserConfig(collectionUrl) {
    if (!collectionUrl) return Promise.resolve(null);
    if (fundraiserConfigCache[collectionUrl]) {
      return Promise.resolve(fundraiserConfigCache[collectionUrl]);
    }

    return fetch(collectionUrl)
      .then(function (response) {
        if (!response.ok) throw new Error('collection fetch failed');
        return response.text();
      })
      .then(function (html) {
        var config = parseFundraiserConfigFromDocument(
          new DOMParser().parseFromString(html, 'text/html')
        );
        if (config) fundraiserConfigCache[collectionUrl] = config;
        return config;
      })
      .catch(function () {
        return null;
      });
  }

  function resolveFundraiserCollectionUrl(cart) {
    var attrs = (cart && cart.attributes) || {};
    if (attrs['Fundraiser Collection URL']) return attrs['Fundraiser Collection URL'];

    var pageConfig = document.getElementById('ssb-fundraiser-page-config');
    if (pageConfig) {
      try {
        var parsed = JSON.parse(pageConfig.textContent);
        if (parsed && parsed.collectionUrl) return parsed.collectionUrl;
      } catch (err) {}
    }

    var defaultEl = document.querySelector('[data-ssb-fundraiser-default-collection-url]');
    if (defaultEl && defaultEl.dataset.ssbFundraiserDefaultCollectionUrl) {
      return defaultEl.dataset.ssbFundraiserDefaultCollectionUrl;
    }

    var productForm = document.querySelector('product-form[data-ssb-fundraiser-collection-url]');
    if (productForm && productForm.dataset.ssbFundraiserCollectionUrl) {
      return productForm.dataset.ssbFundraiserCollectionUrl;
    }

    return '';
  }

  function parseFundraiserDisplayDate(displayText) {
    if (!displayText) return null;

    var cleaned = String(displayText).replace(/\s*\([^)]+\)\s*$/, '').trim();
    var year = new Date().getFullYear();
    var parsed = new Date(cleaned + ', ' + year);

    if (isNaN(parsed.getTime())) {
      parsed = new Date(cleaned);
    }
    if (isNaN(parsed.getTime())) return null;

    var today = new Date();
    today.setHours(0, 0, 0, 0);
    if (parsed < today) parsed.setFullYear(parsed.getFullYear() + 1);

    return parsed;
  }

  function looksLikePickeasySubjectDate(text) {
    return /^[A-Za-z]{3}, \d{1,2} [A-Za-z]{3} \d{4}$/.test(String(text || '').trim());
  }

  function formatFundraiserOrderDueDate(displayText) {
    if (!displayText) return '';
    if (looksLikePickeasySubjectDate(displayText)) return String(displayText).trim();

    var parsed = parseFundraiserDisplayDate(displayText);
    if (!parsed) return displayText;

    var weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return (
      weekdays[parsed.getDay()] +
      ', ' +
      parsed.getDate() +
      ' ' +
      months[parsed.getMonth()] +
      ' ' +
      parsed.getFullYear()
    );
  }

  function buildFullFundraiserAttributes(config, studentName) {
    var pickupDate = (config && config.pickupDateDisplay) || '';
    var pickupLocation = (config && config.pickupLocation) || '';
    studentName = studentName || '';

    return {
      'Fundraiser Name': (config && config.fundraiserName) || '',
      'Fundraiser Collection URL': (config && config.collectionUrl) || '',
      'Student Name': studentName,
      'Fundraiser Pickup Date Display': pickupDate,
      'Fundraiser Pickup Location': pickupLocation,
      'Order Due Date': formatFundraiserOrderDueDate(pickupDate),
      'Order Due Time': '',
      'Order Fulfillment Type': 'Fundraiser',
      'Order Location': pickupLocation
    };
  }

  function fundraiserAttrsNeedUpdate(cartAttrs, desired) {
    cartAttrs = cartAttrs || {};

    for (var key in desired) {
      if (!Object.prototype.hasOwnProperty.call(desired, key)) continue;
      if (String(cartAttrs[key] || '') !== String(desired[key] || '')) return true;
    }
    return false;
  }

  function syncFundraiserCheckoutFormFields(attrs) {
    if (!attrs) return;

    var map = {
      'Order Due Date': 'ssb-attr-order-due-date',
      'Order Due Time': 'ssb-attr-order-due-time',
      'Order Fulfillment Type': 'ssb-attr-order-fulfillment-type',
      'Order Location': 'ssb-attr-order-location',
      'Fundraiser Name': 'ssb-attr-fundraiser-name',
      'Fundraiser Collection URL': 'ssb-attr-fundraiser-collection-url',
      'Student Name': 'ssb-attr-student-name',
      'Fundraiser Pickup Date Display': 'ssb-attr-fundraiser-pickup-date',
      'Fundraiser Pickup Location': 'ssb-attr-fundraiser-pickup-location'
    };

    Object.keys(map).forEach(function (key) {
      var el = document.getElementById(map[key]);
      if (!el) return;
      if (attrs[key] == null) return;
      el.value = String(attrs[key]);
    });
  }

  function updateCartAttributes(desired) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: desired })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (updatedCart) {
        setCartCache(updatedCart);
        syncFundraiserCheckoutFormFields(updatedCart.attributes || desired);
        return updatedCart;
      });
  }

  function stampFundraiserConfig(config, studentName, cart) {
    if (!config) return Promise.resolve(cart);
    var desired = buildFullFundraiserAttributes(config, studentName);
    if (cart && !fundraiserAttrsNeedUpdate(cart.attributes, desired)) {
      return Promise.resolve(cart);
    }
    return updateCartAttributes(desired);
  }

  function cartIsFundraiserCheckout(cart) {
    if (!cart) return false;
    var attrs = cart.attributes || {};
    if (attrs['Fundraiser Name'] || attrs['Fundraiser Collection URL']) return true;
    return isFundraiserOnlyCart(cart);
  }

  function buildFullFundraiserAttributesFromCart(cart, config) {
    if (config) {
      return buildFullFundraiserAttributes(config, (cart && cart.attributes && cart.attributes['Student Name']) || '');
    }

    var attrs = (cart && cart.attributes) || {};
    var pickupDateDisplay = attrs['Fundraiser Pickup Date Display'] || '';
    var pickupLocation = attrs['Fundraiser Pickup Location'] || attrs['Order Location'] || '';

    if (!pickupDateDisplay) {
      pickupDateDisplay = attrs['Order Due Date'] || '';
    }

    return {
      'Fundraiser Name': attrs['Fundraiser Name'] || '',
      'Fundraiser Collection URL': attrs['Fundraiser Collection URL'] || '',
      'Student Name': attrs['Student Name'] || '',
      'Fundraiser Pickup Date Display': pickupDateDisplay,
      'Fundraiser Pickup Location': pickupLocation,
      'Order Due Date': formatFundraiserOrderDueDate(pickupDateDisplay),
      'Order Due Time': '',
      'Order Fulfillment Type': 'Fundraiser',
      'Order Location': pickupLocation
    };
  }

  function ensureFundraiserCheckoutAttributes(cart) {
    if (!cartIsFundraiserCheckout(cart)) return Promise.resolve(cart);

    var desired = buildFullFundraiserAttributesFromCart(cart, null);
    if (!desired['Order Due Date'] && !desired['Fundraiser Name']) return Promise.resolve(cart);
    if (!fundraiserAttrsNeedUpdate(cart.attributes, desired)) return Promise.resolve(cart);

    return updateCartAttributes(desired);
  }

  function prepareFundraiserCheckout(existingCart) {
    return resolveCart(existingCart).then(function (cart) {
      if (!cartIsFundraiserCheckout(cart)) return cart;

      var attrs = cart.attributes || {};
      var collectionUrl = resolveFundraiserCollectionUrl(cart);
      var configPromise =
        collectionUrl && (!attrs['Fundraiser Pickup Date Display'] || !attrs['Fundraiser Name'])
          ? fetchFundraiserConfig(collectionUrl)
          : Promise.resolve(null);

      return configPromise
        .then(function (config) {
          var fullAttrs = buildFullFundraiserAttributesFromCart(cart, config);
          return fundraiserAttrsNeedUpdate(cart.attributes, fullAttrs)
            ? updateCartAttributes(fullAttrs)
            : cart;
        })
        .then(function (updatedCart) {
          syncFundraiserCheckoutFormFields((updatedCart && updatedCart.attributes) || {});
          return updatedCart;
        });
    });
  }

  function initFundraiserPage(options) {
    options = options || {};

    function getConfigPromise() {
      if (options.config) return Promise.resolve(options.config);
      if (options.configElement) {
        try {
          return Promise.resolve(JSON.parse(options.configElement.textContent));
        } catch (err) {
          return Promise.resolve(null);
        }
      }
      if (options.collectionUrl) return fetchFundraiserConfig(options.collectionUrl);
      return Promise.resolve(null);
    }

    var input = options.studentInput;
    var feedback = options.feedbackElement;
    var debounceTimer;

    function setFeedback(msg, type) {
      if (!feedback) return;
      feedback.textContent = msg;
      feedback.className =
        (options.feedbackClassBase || 'ssb-fundraiser-credit__feedback') + (type ? ' is-' + type : '');
    }

    function boot() {
      getConfigPromise().then(function (config) {
        if (!config) return;

        return fetchCart().then(function (cart) {
          var saved = cart.attributes && cart.attributes['Student Name'];
          if (saved && input) {
            input.value = saved;
            setFeedback('✓ Credit will go to: ' + saved, 'saved');
          }
          return stampFundraiserConfig(config, saved || '', cart);
        });
      });

      if (!input) return;

      input.addEventListener('input', function () {
        clearTimeout(debounceTimer);
        input.classList.remove('is-invalid');
        setFeedback('');
        debounceTimer = setTimeout(function () {
          var name = input.value.trim();
          getConfigPromise().then(function (config) {
            if (!config) return;
            if (!name) {
              setFeedback('Please enter a student name to ensure they get credit.', 'error');
              input.classList.add('is-invalid');
              return stampFundraiserConfig(config, '');
            }
            return stampFundraiserConfig(config, name).then(function () {
              setFeedback('✓ Credit will go to: ' + name, 'saved');
            });
          });
        }, 500);
      });

      document.addEventListener(
        'submit',
        function (e) {
          var form = e.target;
          if (!form || !form.action || form.action.indexOf('/cart/add') === -1) return;
          var name = input.value.trim();
          if (!name) {
            e.preventDefault();
            input.focus();
            input.classList.add('is-invalid');
            setFeedback('Please enter a student name before adding items to your cart.', 'error');
            input.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        },
        true
      );
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }

  function startPickeasyObserver() {
    if (state.pickeasyObserver) return;

    state.pickeasyObserver = new MutationObserver(function () {
      if (state.fundraiserOnly) {
        hidePickeasy();
      } else if (state.blockReason) {
        hidePickeasy();
      } else if (state.shippingOnly) {
        applyShippingOnlyPickeasyUi();
      }
    });

    var drawerFooter = document.querySelector('cart-drawer .drawer__footer');
    if (drawerFooter) {
      state.pickeasyObserver.observe(drawerFooter, { childList: true, subtree: true });
    }
    state.pickeasyObserver.observe(document.body, { childList: true, subtree: true });
  }

  function schedulePickeasyHideRetry() {
    clearTimeout(state.hideRetryTimer);
    if (!state.fundraiserOnly && !state.blockReason && !state.shippingOnly) return;

    [0, 100, 250, 500, 1000, 2000, 3500].forEach(function (delay) {
      setTimeout(function () {
        if (state.fundraiserOnly) hidePickeasy();
        else if (state.blockReason) hidePickeasy();
        else if (state.shippingOnly) applyShippingOnlyPickeasyUi();
      }, delay);
    });

    state.hideRetryTimer = setTimeout(function () {
      if (state.fundraiserOnly) hidePickeasy();
      else if (state.blockReason) hidePickeasy();
      else if (state.shippingOnly) applyShippingOnlyPickeasyUi();
    }, 5000);
  }

  function setFundraiserAcceleratedCheckoutHidden(hidden) {
    document.querySelectorAll('.cart__dynamic-checkout-buttons, shopify-accelerated-checkout-cart').forEach(function (el) {
      el.style.setProperty('display', hidden ? 'none' : '', 'important');
    });
  }

  function applyCartState(classification) {
    state.fundraiserOnly = classification.fundraiserOnly;
    state.blockReason = classification.blockReason;
    state.shippingOnly = isShippingOnlyCart(classification);

    var drawer = document.querySelector('cart-drawer');

    if (classification.fundraiserOnly) {
      document.body.classList.add('ssb-fundraiser-only-cart');
      hidePickeasy();
      bindFundraiserCheckoutOverride();
      startPickeasyObserver();
      schedulePickeasyHideRetry();
      setCheckoutDisabled(false);
      updateAlerts(null);
      setShippingOnlyMode(false);
      setFundraiserPickupMode(true);
      setFundraiserAcceleratedCheckoutHidden(true);
      if (drawer) drawer.classList.add('ssb-fundraiser-only');
      if (drawer) drawer.classList.remove('ssb-shipping-blocked');
      return fetchCart()
        .then(function (cart) {
          return ensureFundraiserCheckoutAttributes(cart);
        })
        .then(function (cart) {
          syncFundraiserCheckoutFormFields(cart.attributes || {});
          return cart;
        });
    }

    setFundraiserAcceleratedCheckoutHidden(false);
    setFundraiserPickupMode(false);
    document.body.classList.remove('ssb-fundraiser-only-cart');
    if (drawer) drawer.classList.remove('ssb-fundraiser-only');

    if (classification.blockReason) {
      hidePickeasy();
      startPickeasyObserver();
      schedulePickeasyHideRetry();
      setCheckoutDisabled(true, classification.blockReason);
      updateAlerts(classification.blockReason);
      setShippingOnlyMode(false);
      if (drawer) drawer.classList.add('ssb-shipping-blocked');
      return;
    }

    if (state.shippingOnly) {
      showPickeasy();
      applyShippingOnlyPickeasyUi();
      startPickeasyObserver();
      schedulePickeasyHideRetry();
      setShippingOnlyMode(true);
      setCheckoutDisabled(false);
      updateAlerts(null);
      if (drawer) drawer.classList.remove('ssb-shipping-blocked');
      return;
    }

    clearPickupLocalHide();
    showPickeasy();
    setShippingOnlyMode(false);
    setCheckoutDisabled(false);
    updateAlerts(null);
    if (drawer) drawer.classList.remove('ssb-shipping-blocked');
  }

  function setCartCache(cart) {
    cartFetchCache = cart;
    state.lastCart = cart;
  }

  function fetchCart() {
    if (cartFetchCache) {
      return Promise.resolve(cartFetchCache);
    }

    if (cartFetchPromise) {
      return cartFetchPromise;
    }

    cartFetchPromise = fetch('/cart.js')
      .then(function (response) {
        if (!response.ok) throw new Error('cart fetch failed');
        return response.json();
      })
      .then(function (cart) {
        setCartCache(cart);
        return cart;
      })
      .finally(function () {
        cartFetchPromise = null;
      });

    return cartFetchPromise;
  }

  function resolveCart(existingCart) {
    if (existingCart && existingCart.items) {
      setCartCache(existingCart);
      return Promise.resolve(existingCart);
    }

    if (existingCart && existingCart.cartData && existingCart.cartData.items) {
      setCartCache(existingCart.cartData);
      return Promise.resolve(existingCart.cartData);
    }

    return fetchCart();
  }

  function syncCartFulfillment(existingCart) {
    return resolveCart(existingCart)
      .then(function (cart) {
        if (typeof window.ssbSyncSubjSuffix === 'function') {
          window.ssbSyncSubjSuffix(cart);
        }

        return refreshTagMapFromCart(cart).then(function () {
          return classifyCart(cart).then(function (classification) {
            state.lastClassification = classification;

            if (shouldClearFundraiserAttributes(cart, classification)) {
              return clearStaleFundraiserAttributes(cart).then(function (updatedCart) {
                notifyCartContentsRefresh(updatedCart || cart);
                return applyCartState(classification);
              });
            }
            return applyCartState(classification);
          });
        });
      })
      .catch(function () {});
  }

  function scheduleSyncCartFulfillment(existingCart) {
    clearTimeout(syncDebounceTimer);
    syncDebounceTimer = setTimeout(function () {
      syncCartFulfillment(existingCart);
    }, 50);
  }

  function isVariantTagsKnown(item) {
    return variantTagEntry(item.variant_id) !== null;
  }

  function refreshTagMapFromCart(cart) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return Promise.resolve();
    }

    return Promise.all(
      cart.items.map(function (item) {
        if (isVariantTagsKnown(item)) {
          return Promise.resolve();
        }
        return classifyItemTags(item);
      })
    );
  }

  function cartHasFundraiserContext(cart) {
    if (!cart || cartIsEmpty(cart)) return false;

    var attrs = cart.attributes || {};
    if (attrs['Fundraiser Collection URL'] || attrs['Fundraiser Name']) return true;

    return cart.items.some(itemIsFundraiserSync);
  }

  function wouldMixCart(cart, addingShipping) {
    if (!cart || !cart.items || cart.items.length === 0) {
      return Promise.resolve(false);
    }

    if (isFundraiserOnlyCart(cart) || cartHasFundraiserContext(cart)) {
      return Promise.resolve(false);
    }

    var hasShipping = false;
    var hasNonShipping = false;
    var unknownItems = [];

    cart.items.forEach(function (item) {
      var known = variantTagEntry(item.variant_id);
      if (!known) {
        unknownItems.push(item);
        return;
      }
      if (known.shipping) hasShipping = true;
      else hasNonShipping = true;
    });

    function resolveMix() {
      if (addingShipping) return hasNonShipping;
      return hasShipping;
    }

    if (unknownItems.length === 0) {
      return Promise.resolve(resolveMix());
    }

    return Promise.all(
      unknownItems.map(function (item) {
        return classifyItemTags(item);
      })
    ).then(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isShipping) hasShipping = true;
        else hasNonShipping = true;
      });
      return resolveMix();
    });
  }

  function wouldMixFundraiserCart(cart, addingFundraiser, collectionUrl) {
    if (!cart || cartIsEmpty(cart)) {
      return Promise.resolve(false);
    }

    if (addingFundraiser) {
      return refreshTagMapFromCart(cart).then(function () {
        if (hasNonFundraiserItemSync(cart)) return true;

        var cartUrl = (cart.attributes || {})['Fundraiser Collection URL'];
        if (cartUrl && collectionUrl && cartUrl !== collectionUrl) return true;

        return false;
      });
    }

    return Promise.resolve(cartHasFundraiserContext(cart));
  }

  function hideAddError(productForm) {
    var wrapper = productForm.querySelector('[data-ssb-shipping-add-error]');
    if (wrapper) wrapper.hidden = true;
    productForm.handleErrorMessage(false);
  }

  function showAddError(productForm, reason) {
    var wrapper = productForm.querySelector('[data-ssb-shipping-add-error]');
    if (wrapper) {
      wrapper.querySelectorAll('[data-ssb-mix-message]').forEach(function (el) {
        el.hidden = el.dataset.ssbMixMessage !== reason;
      });
      wrapper.hidden = false;
      return;
    }

    var fallbackMessage =
      reason === 'fundraiser' || reason === 'fundraiser-regular'
        ? "This item can't be combined with the other items in your cart. Clear your cart or remove conflicting items to continue."
        : "This item can't be combined with the other fulfillment type in your cart. Clear your cart or remove conflicting items to continue.";

    productForm.handleErrorMessage(fallbackMessage);
  }

  function bindAddErrorActions(productForm) {
    var wrapper = productForm.querySelector('[data-ssb-shipping-add-error]');
    if (!wrapper || wrapper.dataset.ssbBound) return;
    wrapper.dataset.ssbBound = '1';

    wrapper.querySelector('[data-ssb-dismiss-add-error]')?.addEventListener('click', function () {
      hideAddError(productForm);
      productForm.submitButton?.removeAttribute('aria-disabled');
      productForm.submitButton?.classList.remove('loading');
      productForm.querySelector('.loading__spinner')?.classList.add('hidden');
    });

    wrapper.querySelector('[data-ssb-clear-and-add]')?.addEventListener('click', function () {
      fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } })
        .then(function () {
          invalidateCartCache();
          hideAddError(productForm);
          productForm.dataset.ssbSkipMixCheck = 'true';
          productForm.form?.requestSubmit(productForm.submitButton);
        })
        .catch(function () {
          showAddError(productForm, 'fundraiser-regular');
        });
    });
  }

  function cartIsEmpty(cart) {
    return !cart || !cart.items || cart.items.length === 0;
  }

  function canSkipFundraiserMixCheck(addingFundraiser, collectionUrl, cart) {
    cart = cart || state.lastCart || cartFetchCache;

    if (!cart || cartIsEmpty(cart)) return true;

    if (!addingFundraiser) {
      return !cartHasFundraiserContext(cart);
    }

    if (!cartHasFundraiserContext(cart) && !isFundraiserOnlyCart(cart)) return true;

    var cartUrl = (cart.attributes || {})['Fundraiser Collection URL'];
    if (cartUrl && collectionUrl && cartUrl === collectionUrl && !hasNonFundraiserItemSync(cart)) {
      return true;
    }

    return false;
  }

  function canSkipMixCheck(addingShipping, addingFundraiser) {
    if (addingFundraiser) return true;

    var cart = state.lastCart || cartFetchCache;

    if (!cart) {
      return false;
    }

    if (cartIsEmpty(cart)) {
      return true;
    }

    if (cartHasFundraiserContext(cart)) {
      return false;
    }

    var classification = state.lastClassification;
    if (!classification) {
      return false;
    }

    if (addingShipping && !classification.hasNonShippingItems) {
      return true;
    }

    if (!addingShipping && !classification.hasShippingItems) {
      return true;
    }

    return false;
  }

  function finishMixValidation(blocked, productForm, reason) {
    if (!blocked) {
      hideAddError(productForm);
      return true;
    }
    showAddError(productForm, reason);
    return false;
  }

  function getCartPromiseForValidation() {
    return cartFetchPromise || (state.lastCart || cartFetchCache
      ? Promise.resolve(state.lastCart || cartFetchCache)
      : fetchCart());
  }

  function validateFundraiserMix(addingFundraiser, collectionUrl, productForm) {
    if (canSkipFundraiserMixCheck(addingFundraiser, collectionUrl)) {
      hideAddError(productForm);
      return Promise.resolve(true);
    }

    return getCartPromiseForValidation()
      .then(function (cart) {
        return wouldMixFundraiserCart(cart, addingFundraiser, collectionUrl).then(function (blocked) {
          if (!blocked) {
            hideAddError(productForm);
            return true;
          }

          var reason = addingFundraiser ? 'fundraiser-regular' : 'fundraiser-regular';
          if (addingFundraiser) {
            var cartUrl = (cart.attributes || {})['Fundraiser Collection URL'];
            if (cartUrl && collectionUrl && cartUrl !== collectionUrl) {
              reason = 'fundraiser';
            }
          }

          showAddError(productForm, reason);
          return false;
        });
      });
  }

  function resolveStudentNameForStamp() {
    var input =
      document.getElementById('ssb-student-name-product') ||
      document.getElementById('ssb-student-name');
    if (input && input.value.trim()) return input.value.trim();

    var cart = state.lastCart || cartFetchCache;
    return (cart && cart.attributes && cart.attributes['Student Name']) || '';
  }

  function stampFundraiserCollectionContext(collectionUrl) {
    if (!collectionUrl) return Promise.resolve();

    return fetchFundraiserConfig(collectionUrl).then(function (config) {
      if (!config) return null;
      return fetchCart().then(function (cart) {
        return stampFundraiserConfig(config, resolveStudentNameForStamp(), cart);
      });
    });
  }

  function validateAddToCart(addingShipping, productForm) {
    bindAddErrorActions(productForm);

    if (productForm.dataset.ssbSkipMixCheck === 'true') {
      delete productForm.dataset.ssbSkipMixCheck;
      return Promise.resolve(true);
    }

    var addingFundraiser = productForm.dataset.ssbFundraiser === 'true';
    var collectionUrl = productForm.dataset.ssbFundraiserCollectionUrl || '';

    return validateFundraiserMix(addingFundraiser, collectionUrl, productForm)
      .then(function (fundraiserAllowed) {
        if (!fundraiserAllowed) return false;

        var stampPromise =
          addingFundraiser && collectionUrl
            ? stampFundraiserCollectionContext(collectionUrl)
            : Promise.resolve();

        return stampPromise.then(function () {
          if (canSkipMixCheck(addingShipping, addingFundraiser)) {
            hideAddError(productForm);
            return true;
          }

          return getCartPromiseForValidation()
            .then(function (cart) {
              return wouldMixCart(cart, addingShipping);
            })
            .then(function (blocked) {
              var reason = addingShipping ? 'shipping-shippable' : 'shipping-pickup';
              return finishMixValidation(blocked, productForm, reason);
            });
        });
      })
      .catch(function () {
        return true;
      });
  }

  function invalidateCartCache() {
    cartFetchCache = null;
  }

  function initListeners() {
    document.addEventListener('DOMContentLoaded', function () {
      bindFundraiserCartFormCheckout();
      syncCartFulfillment();
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event && event.cartData && event.cartData.items) {
          setCartCache(event.cartData);
        } else {
          invalidateCartCache();
        }
        scheduleSyncCartFulfillment(event);
      });
    }

    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (mutation.attributeName === 'class' && drawer.classList.contains('active')) {
            scheduleSyncCartFulfillment();
          }
        });
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }

  window.ssbCartFulfillment = window.ssbCartFulfillment || {};
  window.ssbCartFulfillment.tagMap = window.ssbCartFulfillment.tagMap || {};
  window.ssbCartFulfillment.sync = syncCartFulfillment;
  window.ssbCartFulfillment.getCart = fetchCart;
  window.ssbCartFulfillment.setCartCache = setCartCache;
  window.ssbCartFulfillment.invalidateCartCache = invalidateCartCache;
  window.ssbCartFulfillment.validateAddToCart = validateAddToCart;
  window.ssbCartFulfillment.prepareFundraiserCheckout = prepareFundraiserCheckout;
  window.ssbCartFulfillment.initFundraiserPage = initFundraiserPage;
  window.ssbCartFulfillment.fetchFundraiserConfig = fetchFundraiserConfig;
  window.ssbCartFulfillment.stampFundraiserConfig = stampFundraiserConfig;
  window.ssbCartFulfillment.schedulePickeasyHideRetry = schedulePickeasyHideRetry;

  window.ssbSyncFundraiserCartDrawer = syncCartFulfillment;
  window.ssbScheduleFundraiserPickeasyHide = schedulePickeasyHideRetry;

  initListeners();
})();
